/*
  Профиль для глубоких компонентов (аудит 26.10.2026, причина 2).

  Страницы получают `profile` пропсом, но формы настроек, загрузки и мастера лежат на
  три-четыре уровня ниже, и тянуть профиль через каждый из них значило бы переписать
  десятки сигнатур ради одной проверки. Каркас кладёт профиль сюда один раз.
*/

import { createContext, useContext } from 'react'

import { can, has } from './access.js'

const AccessContext = createContext(null)

export const AccessProvider = AccessContext.Provider

/** `readonly` - лицензия не оплачена; `can(право)` - можно ли действовать сейчас. */
export function useAccess() {
  const profile = useContext(AccessContext)
  return {
    readonly: Boolean(profile?.is_readonly),
    can: (permission) => can(profile, permission),
    has: (permission) => has(profile, permission),
  }
}

/*
  Содержимое остаётся видимым, но не нажимается, пока лицензия не оплачена: данные
  «видны и выгружаются», действия закрыты (§3.1.6). `fieldset disabled` гасит все
  поля и кнопки внутри разом, включая свой выпадающий список (его разметка - кнопка).
*/
/*
  Форма целиком: `form > fieldset.lock > содержимое`. Вкладки и листание, которым нужно
  работать и при неоплаченной лицензии, ставятся СНАРУЖИ формы - `fieldset disabled`
  гасит каждую кнопку внутри, включая те, что только переключают вид.
*/
export function LockedForm({ busy = false, children, ...rest }) {
  return (
    <form {...rest}>
      <Lock busy={busy}>{children}</Lock>
    </form>
  )
}

export function Lock({ busy = false, children }) {
  const { readonly } = useAccess()
  // `busy` - идёт сохранение: повторное нажатие даст вторую запись (аудит 26.10.2026,
  // причина 4), поэтому форма гаснет на время запроса.
  // `role="none"`: замок - способ погасить поля разом, а не смысловая группа. Без этого
  // читалка экрана объявляла бы «группа» вокруг каждой кнопки, а выбор групп по роли
  // (жильцы бланка в сверке скана) считал бы замки жильцами (28.10.2026, строка 291).
  return (
    <fieldset className="lock" role="none" disabled={readonly || busy}>
      {children}
    </fieldset>
  )
}
